/* eslint-disable no-restricted-globals */
import { RouteComponentProps } from "@reach/router";
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";

import Button from "../components/button/Button";
import Table, { Column } from "../components/table/Table";
import LayoutContext from "../context/layout-context";
import { initialPaginationData } from "../utils/constants";
import ContextMenu, {
  ContextOption
} from "../components/context-menu/ContextMenu";
import { BooleanFilter } from "../utils/helpers/types-helpers";
import Modal, { ModalProps } from "../components/modal/Modal";
import { resources } from "../utils/resources";
import Input from "../components/input/Input";
import { ClientCreate, ClientCreations } from "../utils/helpers/types";
import {
  createClient,
  getClient,
  getClients,
  updateClient
} from "../utils/helpers/data/client";
import styles from "./self-service.module.scss";

const Client: FunctionComponent<RouteComponentProps> = () => {
  const [loading, setLoading] = useState(false);
  const [reset, setReset] = useState(false);
  const [pagination, setPagination] = useState({ ...initialPaginationData });
  const [clientOnView, setClientOnView] = useState<ClientCreations | null>(
    null
  );
  const { notify, confirm } = useContext(LayoutContext);
  const [client, setClient] = useState<ClientCreations[]>([]);
  const [clientOnEdit, setClientOnEdit] = useState<ClientCreations | null>(
    null
  );
  const [showAddEditModal, setShowAddEditModal] = useState(false);

  const resetFilters = () => {
    setPagination({ ...pagination, pageNumber: 1 });
    setReset(true);
  };

  const displayAddEditModal = async (
    clientCreation: ClientCreations | null
  ) => {
    if (clientCreation) {
      setLoading(true);
      const { data, error, message } = await getClient(clientCreation.id);
      setLoading(false);
      if (error || !data) {
        notify("error", `Unable to fetch client details: ${message}`);
        return;
      }
      setClientOnEdit(data);
    } else {
      setClientOnEdit(null);
    }
    setShowAddEditModal(true);
  };

  const dismissAddEditModal = () => {
    setClientOnEdit(null);
    setShowAddEditModal(false);
  };

  const fetchClient = async (
    pageNumber = pagination.pageNumber,
    pageSize = pagination.pageSize
  ) => {
    setLoading(true);
    const { data, error, message } = await getClients(pageNumber, pageSize);
    setLoading(false);

    if (error || !data) {
      notify("error", `Unable to fetch clients: ${message}`);
      return;
    }

    setClient(data.records);
    setPagination({
      ...pagination,
      pageNumber,
      total: data.total
    });

    setReset(false);
  };

  //   const handleViewReport = async () => {
  //     setLoading(true);
  //     try {
  //       setPagination({ ...pagination, pageNumber: 1 });
  //       await fetchClient(pagination.pageNumber);
  //       setLoading(false);
  //     } catch (error: any) {
  //       setLoading(false);
  //       notify("error", "An error occurred while fetching client.", error);
  //     }
  //   };

  useEffect(() => {
    fetchClient();
  }, [pagination.pageSize]);

  useEffect(() => {
    if (!reset) {
      fetchClient();
    } else {
      setPagination({ ...initialPaginationData });
      setReset(false);
    }
  }, [reset]);

  const handleOperation = async (operation: any, id: any) => {
    setLoading(true);
    let result;

    switch (operation) {
      case "view":
        result = await getClient(id);
        if (!result.error) {
          setClientOnView(result.data);
        }
        break;
      default:
        setLoading(false);
        return;
    }

    setLoading(false);

    if (result.error) {
      notify("error", `Unable to ${operation} client: ${result.message}`);
      return;
    }

    notify("success", `client ${operation}d successfully`);
  };

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      })
      .replace(/\//g, "-");
    return formattedDate;
  };
  const columns: Column<ClientCreations>[] = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName"
    },
    {
      title: "Client Code",
      dataIndex: "clientCode",
      key: "clientCode"
    },
    {
      title: "Billing Status",
      dataIndex: "billingStatus",
      key: "billingStatus"
    },
    {
      title: "Billing Cycle",
      dataIndex: "billingCycle",
      key: "billingCycle"
    },
    {
      title: "Settlement Bank",
      dataIndex: "settlementBank",
      key: "settlementBank"
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      render: (createdDate: string) => formatDate(createdDate)
    },

    {
      title: "",
      render: (_: unknown, client: ClientCreations) => {
        const options: ContextOption[] = [
          {
            label: "View Details",
            onClick: () => {
              handleOperation("view", client.id);
            }
          },
          {
            label: "Edit",
            onClick: () => {
              displayAddEditModal(client);
            }
          }
        ].filter(BooleanFilter) as ContextOption[];
        return <ContextMenu options={options} />;
      }
    }
  ];
  return (
    <main>
      <h1 className="title vertical-margin spaced thin">Client</h1>

      <br />

      <div className="vertical-margin flex end">
        <Button onClick={() => displayAddEditModal(null)}>Create Client</Button>
      </div>

      <Table
        dataSource={client || []}
        columns={columns}
        pagination={pagination}
        onPageChange={fetchClient}
        onPageSizeChange={pageSize =>
          setPagination({ ...pagination, pageSize, pageNumber: 1 })
        }
        loading={loading}
        className="smaller"
      />

      <ClientViewModal
        client={clientOnView}
        cancel={() => setClientOnView(null)}
        visible={Boolean(clientOnView)}
      />

      <AddEditModal
        visible={showAddEditModal}
        clientOnEdit={clientOnEdit}
        cancel={dismissAddEditModal}
        refresh={(stayOnPage: any) =>
          fetchClient(stayOnPage ? pagination.pageNumber : 1)
        }
        fetchData={fetchClient}
      />
    </main>
  );
};

const blankappService: ClientCreate = {
  clientId: "",
  clientName: "",
  clientCode: "",
  billingCycle: "",
  settlementAccount: "",
  billingStatus: "",
  settlementBank: "",
  lastBillDate: ""
};

const AddEditModal: FunctionComponent<
  ModalProps & {
    clientOnEdit: ClientCreations | null;
    refresh: (stayOnPage: boolean) => void;
    cancel: () => void;
    fetchData: () => void;
  }
> = ({ visible, cancel, clientOnEdit, refresh, fetchData }) => {
  const [formData, setFormData] = useState<ClientCreate>({
    ...blankappService,
    ...clientOnEdit
  });

  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useContext(LayoutContext);

  const handleChange = (key: keyof ClientCreate, value: unknown) => {
    setFormData(prevFormData => ({ ...prevFormData, [key]: value }));
  };

  useEffect(() => {
    setFormData(
      clientOnEdit
        ? { ...blankappService, ...clientOnEdit }
        : { ...blankappService }
    );
  }, [visible, clientOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setIsSaving(true);

    // const nameRegex = /^[A-Za-z]+$/;

    // if (!nameRegex.test(formData.name)) {
    //   notify("error", "Name must contain only letters");
    //   setIsSaving(false);
    //   return;
    // }

    // if (formData.name.length < 3) {
    //   notify("error", "Name must be at least 3 characters long");
    //   setIsSaving(false);
    //   return;
    // }

    let id: number | undefined;

    if (clientOnEdit && typeof clientOnEdit.id === "number") {
      id = clientOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message } = await updateClient(id, {
        ...formData
      });

      if (error) {
        notify("error", `Unable to update client: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully updated client");
    } else {
      const { error, message } = await createClient({
        ...formData
      });

      if (error) {
        notify("error", `Unable to create client: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully created client");
    }
    cancel?.();
    refresh(false);
    setIsSaving(false);
    fetchData();
  };

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Client Id</span>
          <Input
            value={formData.clientId}
            onChange={value => handleChange("clientId", value)}
            placeholder="Enter clientId"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Client Name</span>
          </div>
          <Input
            value={formData.clientName}
            onChange={value => handleChange("clientName", value)}
            placeholder="Enter client name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Client Code</span>
          </div>
          <Input
            value={formData.clientCode}
            onChange={value => handleChange("clientCode", value)}
            placeholder="Enter client code"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Billing Cycle</span>
          </div>
          <Input
            value={formData.billingCycle}
            onChange={value => handleChange("billingCycle", value)}
            placeholder="Enter billing cycle"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Billing Status</span>
          </div>
          <Input
            value={formData.billingStatus}
            onChange={value => handleChange("billingStatus", value)}
            placeholder="Enter billing status"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Settlement Account</span>
          </div>
          <Input
            value={formData.settlementAccount}
            onChange={value => handleChange("settlementAccount", value)}
            placeholder="Enter settlement account"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Settlement Bank</span>
          </div>
          <Input
            value={formData.settlementBank}
            onChange={value => handleChange("settlementBank", value)}
            placeholder="Enter settlement bank"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Last Bill Date</span>
          </div>
          <Input
            value={formData.lastBillDate}
            onChange={value => handleChange("lastBillDate", value)}
            placeholder="Enter last bill date"
            responsive
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            {clientOnEdit ? "Edit client" : "Create client"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

interface clientModalProps {
  client: ClientCreations | null;
  cancel: () => void;
  visible: boolean;
}

const ClientViewModal: FunctionComponent<clientModalProps> = ({
  client: _wallet,
  cancel,
  visible
}) => {
  const [loading] = useState(false);
  const [client, setClient] = useState<ClientCreations | null>(_wallet);

  useEffect(() => {
    setClient(_wallet);
  }, [_wallet]);

  const formatDate = (date: string): string => {
    const formattedDate = new Date(date)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      })
      .replace(/\//g, "-");
    return formattedDate;
  };

  return (
    <Modal visible={visible} cancel={cancel} ignoreBackdropClicks>
      {loading ? (
        <img
          alt="loading"
          src={resources.images.LOADING_TYPING}
          className="generic-icon xxl"
        />
      ) : (
        <section className={styles.details}>
          <div className={styles.detail}>
            <span className={styles.key}>Id:</span>
            <span className={styles.value}>{client?.id || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Client Id:</span>
            <span className={styles.value}>{client?.clientId || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Client Name:</span>
            <span className={styles.value}>{client?.clientName || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Client Code:</span>
            <span className={styles.value}>{client?.clientCode || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Created Date:</span>
            <span className={styles.value}>
              {client?.createdDate ? formatDate(client.createdDate) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Next Bill Date:</span>
            <span className={styles.value}>
              {client?.nextBillDate ? formatDate(client.nextBillDate) : "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Billing Cycle:</span>
            <span className={styles.value}>
              {client?.billingCycle || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Billing Status:</span>
            <span className={styles.value}>
              {client?.billingStatus || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Offset Days:</span>
            <span className={styles.value}>{client?.offsetDays || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Settlement Account:</span>
            <span className={styles.value}>
              {client?.settlementAccount || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Settlement Bank:</span>
            <span className={styles.value}>
              {client?.settlementBank || "N/A"}
            </span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Updated By:</span>
            <span className={styles.value}>{client?.updatedBy || "N/A"}</span>
          </div>

          <div className={styles.detail}>
            <span className={styles.key}>Updated Date:</span>
            <span className={styles.value}>
              {client?.updatedDate ? formatDate(client.updatedDate) : "N/A"}
            </span>
          </div>
        </section>
      )}
    </Modal>
  );
};
export default Client;
